import { type Registry as Services, service } from '@ember/service';
import Model, {
  attr,
  belongsTo,
  type HasMany,
  hasMany,
} from '@ember-data/model';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import { Type } from '@warp-drive/core-types/symbols';
import type { Hotel } from 'frontend-utils/models';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import { convertKilometersToMiles } from 'frontend-utils/utils/distance';
import type { Day } from 'lumen/models';
import type { HoteldateTransformReturn } from 'lumen/transforms/hoteldate';

export const EVENTS_STATUSES = {
  ACCEPTED: 'accepted',
  AUTO_ACCEPTED: 'auto-accepted', // Legacy status. There is no visual difference in FE but BE still sends this status
  DECLINED: 'rejected',
  SUGGESTED: 'suggested',
  EMPTY: '', // Events without status are considered suggested
} as const;

export const EVENTS_CATEGORIES = {
  LOCAL: 'local',
  MUSIC: 'music',
  SPORT: 'sport',
  CONFERENCE: 'conference',
  INHOUSE: 'inhouse',
  PERFORMING_ARTS: 'performing_arts',
  OTHER: 'other',
};

export type Status = (typeof EVENTS_STATUSES)[keyof typeof EVENTS_STATUSES];

export default class Event extends Model {
  [Type] = 'event' as const;

  @belongsTo('hotel', { async: false, inverse: null })
  declare hotel: Hotel;

  @hasMany('day', { async: false, inverse: 'events' })
  declare days: HasMany<Day>;

  @attr('boolean') declare areExactCoordinates: boolean;
  @attr('string') declare category: string;
  @attr('dayjs') declare createdTimestamp: DayjsTransformReturn;
  @attr('string') declare description: string;
  @attr('number') declare distance: number;
  @attr('hoteldate') declare endDate: NonNullable<HoteldateTransformReturn>;
  @attr('number') declare exhibitors: number;
  @attr('number') declare importance: number;
  @attr('boolean') declare isCancelled: boolean;
  @attr('boolean') declare isNewEvent: boolean;
  @attr('boolean') declare isPopoverActive: boolean;
  @attr('boolean') declare isPrivate: boolean;
  @attr() declare labels: string[] | undefined;
  @attr('dayjs') declare lastUpdateTimestamp: DayjsTransformReturn;
  @attr('number') declare lat: number;
  @attr('string') declare location: string;
  @attr('number') declare lon: number;
  @attr('string') declare name: string;
  @attr('number') declare numberOfDays: number;
  @attr('string') declare placesId: string | undefined;
  @attr() declare placesResponse: unknown;
  @attr('hoteldate') declare startDate: NonNullable<HoteldateTransformReturn>;
  @attr('string')
  declare status: (typeof EVENTS_STATUSES)[keyof typeof EVENTS_STATUSES];
  @attr('number') declare timesAccepted: number;
  @attr('string') declare url: string;
  @attr('number') declare visitors: number;

  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  /**
   * IMPORTANT: When adding properties to this model, be sure to check
   * the `event` search results usage in the `hotel.events` controller.
   */
  added = false;
  class = '';
  declined = false;
  justAdded = false;

  // These are events that are visible by default in the calendar & in other places in the app
  get defaultVisible(): boolean {
    return this.isAccepted || this.isSuggested;
  }

  get displayCategory(): string {
    return this.intl.t(`events.category.${this.category}`).toString();
  }

  get displayName(): string {
    return (
      this.name +
      (this.isCancelled ? ` (${this.intl.t('general.cancelled')})` : '')
    );
  }

  get distanceFormatted(): string | null {
    if (this.distance) {
      const kilometers = this.intl.formatNumber(this.distance, {
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
      });

      return this.hotelAndMarketService.useImperialSystem
        ? `${Math.round(convertKilometersToMiles(this.distance))} mi`
        : `${kilometers} km`;
    }

    return null;
  }

  // Easy format for displaying event duration
  get formattedDays(): string {
    if (this.startDate.isSame(this.endDate, 'day')) {
      return this.startDate.format('L');
    }
    return `${this.startDate.format('L')} - ${this.endDate.format('L')}`;
  }

  get formattedDaysInWeekdayAndMonthAndDayMedium(): string {
    const format =
      this.dates.partialDateFormat.WEEKDAY_AND_MONTH_AND_DAY_MEDIUM;

    if (this.startDate.isSame(this.endDate, 'day')) {
      return this.startDate.format(format);
    }
    return `${this.startDate.format(format)} - ${this.endDate.format(format)}`;
  }

  get icon(): IconName {
    switch (this.category) {
      case EVENTS_CATEGORIES.CONFERENCE:
        return 'events-conference';
      case EVENTS_CATEGORIES.PERFORMING_ARTS:
        return 'events-performing-arts';
      case EVENTS_CATEGORIES.MUSIC:
        return 'events-music';
      case EVENTS_CATEGORIES.SPORT:
        return 'events-sports';
      default:
        return 'calendar-accepted';
    }
  }

  get isAccepted(): boolean {
    return (
      this.status === EVENTS_STATUSES.ACCEPTED ||
      this.status === EVENTS_STATUSES.AUTO_ACCEPTED
    );
  }

  get isDeclined(): boolean {
    return this.status === EVENTS_STATUSES.DECLINED;
  }

  get isSingleDay(): boolean {
    return this.startDate.isSame(this.endDate, 'day');
  }

  get isSuggested(): boolean {
    return (
      this.status === EVENTS_STATUSES.SUGGESTED ||
      this.status === EVENTS_STATUSES.EMPTY
    );
  }

  get length(): number {
    return this.endDate.diff(this.startDate, 'day');
  }

  get startDateAsUnix(): number {
    return this.startDate.unix();
  }

  get statusOrder(): number {
    if (this.isAccepted) {
      return 1;
    }
    if (this.isSuggested) {
      return 2;
    }
    if (this.isDeclined) {
      return 3;
    }
    return 4;
  }

  get visitorsFormatted(): string | null {
    if (this.visitors) {
      return this.intl.formatNumber(this.visitors, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      });
    }
    return null;
  }
}
